if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.form.querySelector('[name="id"]').disabled = false;
        this.cartDrawer = document.querySelector('cart-drawer');
        this.submitBtn = this.querySelector('[name="add"]');

        if (theme.settings.afterAtc !== 'no-js') {
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }
      }
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const formData = new FormData(this.form);
      const variantId = Number(formData.get('id'));
      const quantityToAdd = ProductForm.getFormQuantity(formData);

      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      try {
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();

        const existingLineItem = ProductForm.getExistingLineItem(oldCartData, formData);
        let response;
        let data;
        let newCartData;

        if (existingLineItem) {
          const changeFormData = new FormData();
          changeFormData.append('id', existingLineItem.key);
          changeFormData.append('quantity', existingLineItem.quantity + quantityToAdd);
          changeFormData.append('sections_url', window.location.pathname);
          changeFormData.append('sections', sections);

          response = await fetch(theme.routes.cartChange, {
            method: 'POST',
            headers: {
              Accept: 'application/javascript',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: changeFormData
          });

          data = await response.json();
          newCartData = data;
        } else {
          response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
          data = await response.json();
        }

        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }

        if (data.status) this.setErrorMsgState(error);

        if (!response.ok) throw new Error(response.status);

        if (theme.settings.afterAtc === 'page') {
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          ProductForm.updateCartIcon(data);

          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          setTimeout(() => {
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(
                data,
                !quickAddDrawer && theme.settings.afterAtc === 'drawer'
              );
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                if (cartItems.dataset.empty === 'true') {
                  window.location.reload();
                } else {
                  cartItems.refresh();
                }
              }
            }
          }, 700);
        }

        if (!newCartData) {
          const newCartResponse = await fetch(`${theme.routes.cart}.js`);
          if (!newCartResponse.ok) throw new Error(newCartResponse.status);
          newCartData = await newCartResponse.json();
        }

        if (existingLineItem) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId,
              oldQuantity: existingLineItem.quantity,
              newQuantity: existingLineItem.quantity + quantityToAdd
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId
            }
          }));
        }
      } catch (error) {
        console.log(error);

        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));

        if (this.cartDrawer) this.cartDrawer.refresh(true);
      } finally {
        this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');

        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 1400);
      }
    }

    /**
     * Returns the product quantity submitted by the product form.
     * @param {FormData} formData - Product form data.
     * @returns {number}
     */
    static getFormQuantity(formData) {
      const quantity = parseInt(formData.get('quantity'), 10);
      return Number.isNaN(quantity) || quantity < 1 ? 1 : quantity;
    }

    /**
     * Checks if the form contains visible line item properties.
     * Visible properties are not merged automatically because they can represent personalization.
     * Hidden properties beginning with _ are ignored for merge purposes.
     * @param {FormData} formData - Product form data.
     * @returns {boolean}
     */
    static formHasVisibleLineItemProperties(formData) {
      for (const [key, value] of formData.entries()) {
        const propertyName = ProductForm.getPropertyName(key);

        if (!propertyName) continue;
        if (propertyName.startsWith('_')) continue;
        if (String(value).trim() !== '') return true;
      }

      return false;
    }

    /**
     * Extracts a line item property name from a FormData key.
     * @param {string} key - FormData key.
     * @returns {string|null}
     */
    static getPropertyName(key) {
      const match = key.match(/^properties\[(.*)\]$/);
      return match ? match[1] : null;
    }

    /**
     * Finds an existing cart line that can safely be incremented instead of adding a duplicate line.
     * @param {object} cart - Cart JSON.
     * @param {FormData} formData - Product form data.
     * @returns {object|null}
     */
    static getExistingLineItem(cart, formData) {
      if (!cart || !Array.isArray(cart.items)) return null;
      if (ProductForm.formHasVisibleLineItemProperties(formData)) return null;

      const variantId = Number(formData.get('id'));
      const sellingPlanId = String(formData.get('selling_plan') || '');

      return cart.items.find((item) => {
        if (Number(item.variant_id) !== variantId) return false;

        const itemSellingPlanId = item.selling_plan_allocation
          ? String(
            item.selling_plan_allocation.selling_plan?.id
            || item.selling_plan_allocation.selling_plan_id
            || ''
          )
          : '';

        return itemSellingPlanId === sellingPlanId;
      }) || null;
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');

      if (cartIconBubble && response.sections && response.sections['cart-icon-bubble']) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;

      if (error) {
        this.errorMsg.innerHTML = '';

        const errorArray = Array.isArray(error) ? error : [error];

        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}